import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search as SearchIcon, Loader2 } from "lucide-react";
import { publicApi } from "../../hooks/useApi";

const RESULT_GROUPS = [
  { key: "episodes", label: "Podcast", base: "/listen" },
  { key: "books", label: "Books", base: "/read" },
  { key: "audio", label: "Audiobooks", base: "/audio" },
  { key: "lessons", label: "MuscleSchool", base: "/learn/lessons" },
  { key: "gold", label: "Gold", base: "/gold" },
];

export function HeaderSearch({ className = "", testId = "header-search" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < 2) {
      setResults(null);
      setError(false);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(false);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await publicApi.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(data);
      } catch (e) {
        setError(true);
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const submit = useCallback(() => {
    const query = q.trim();
    if (query.length < 2) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  }, [q, navigate]);

  const hasAny = results && RESULT_GROUPS.some((g) => results[g.key]?.length > 0);
  const query = q.trim();

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 bg-black border border-white/25 focus-within:border-white/60 px-3.5 h-10 transition-colors duration-200">
        <input
          data-testid={testId}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Search podcasts, books, and more..."
          aria-label="Search podcasts, books, and more"
          className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder:text-white/70 focus:outline-none"
        />
        <button
          data-testid={`${testId}-submit`}
          onClick={submit}
          aria-label="Submit search"
          className="text-white/60 hover:text-white transition-colors duration-200 shrink-0"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
        </button>
      </div>

      {open && query.length >= 2 && (
        <div data-testid={`${testId}-dropdown`} className="absolute left-0 right-0 mt-2 bg-black border border-white/15 shadow-2xl max-h-[70vh] overflow-y-auto z-50">
          {error && (
            <p data-testid={`${testId}-error`} className="px-4 py-4 text-sm text-white/75">
              Search is unavailable right now. Please try again shortly.
            </p>
          )}
          {!error && loading && !results && (
            <p data-testid={`${testId}-loading`} className="px-4 py-4 text-sm text-white/75">Searching…</p>
          )}
          {!error && results && !hasAny && (
            <p data-testid={`${testId}-empty`} className="px-4 py-4 text-sm text-white/75">No results for &ldquo;{query}&rdquo;.</p>
          )}
          {!error && results && hasAny && (
            <div className="py-2">
              {RESULT_GROUPS.map((g) => (
                results[g.key]?.length > 0 && (
                  <div key={g.key} className="px-4 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1.5">{g.label}</p>
                    <ul>
                      {results[g.key].slice(0, 5).map((item) => (
                        <li key={item.id}>
                          <Link
                            to={g.key === "gold" ? "/gold" : `${g.base}/${item.id}`}
                            onClick={() => setOpen(false)}
                            data-testid={`${testId}-result-${item.id}`}
                            className="block py-1.5 text-sm text-white/80 hover:text-white truncate"
                          >
                            {item.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
              <Link
                to={`/search?q=${encodeURIComponent(query)}`}
                onClick={() => setOpen(false)}
                data-testid={`${testId}-view-all`}
                className="block px-4 py-3 text-xs uppercase tracking-wide text-white/75 hover:text-white border-t border-white/10"
              >
                View all results →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
